/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { MockedProvider, MockedResponse } from "@apollo/client/testing";
import OktaAuth from "@okta/okta-auth-js";
import { PL_DEFAULT_THEME, ThemeProvider } from "@powerledger/ui-component-lib";
import { render as rtlRender } from "@testing-library/react";
import { renderHook as rtlRenderHook } from "@testing-library/react-hooks/dom/pure";
import * as ConfigCat from "configcat-react";
import { Formik } from "formik";
import { createMemoryHistory } from "history";
import { JSXElementConstructor, ReactElement, Suspense } from "react";
import { Router } from "react-router-dom";

import * as globalMocks from "./__mocks__/apollo-mocks";
import { LoadingOverlayIndicator } from "./app/components/loading-overlay-indicator";
import { demoAccount } from "./app/container/apollo-client/demo-data";
import { AppContext, AppContextType } from "./app/context";
import { FeatureToggleKey, FeatureToggleKeyMap } from "./app/hooks";
import { UserRoles } from "./app/lib/role-based-access-control";
import { ModalProvider } from "./app/modal-provider";
import { OktaSecurity } from "./app/navigation/okta-security";
import { OnboardingStepperHandler } from "./app/pages/public/signup/onboarding";
import { OnboardingStepperContext } from "./app/pages/public/signup/onboarding/context";

type ReactChildren = ReactElement<any, string | JSXElementConstructor<any>>;

export const appHistory = createMemoryHistory();

export const renderHook = <TProp extends { children: ReactChildren }, TResult>(
  hook: (props: TProp) => TResult,
  mocks?: Array<MockedResponse>,
  formikValues: Record<string, any> = {},
  history = appHistory,
) => {
  const wrapper = ({ children }: { children: ReactChildren }) => (
    <Router navigator={history} location={history.location}>
      <Formik initialValues={formikValues} onSubmit={() => {}}>
        <MockedProvider mocks={mocks}>{children}</MockedProvider>
      </Formik>
    </Router>
  );

  return rtlRenderHook<TProp, TResult>(hook, { wrapper });
};

export const renderWithProviders = (
  ui: ReactChildren,
  {
    graphqlMocks = [],
    contextValue = mockAppContextValue,
    oktaAuth = oktaAuthLoggedIn,
    ...renderOptions
  }: { graphqlMocks?: Array<MockedResponse>; contextValue?: AppContextType; oktaAuth?: OktaAuth } = {},
  history = appHistory,
) => {
  const wrapper = ({ children }: { children: ReactChildren }) => (
    <ThemeProvider theme={PL_DEFAULT_THEME}>
      <AppContext.Provider value={contextValue}>
        <MockedProvider addTypename={false} mocks={graphqlMocks}>
          <Router navigator={history} location={history.location}>
            <ModalProvider>
              <Suspense fallback={<LoadingOverlayIndicator />}>
                <OktaSecurity oktaAuth={oktaAuth}>{children}</OktaSecurity>
              </Suspense>
            </ModalProvider>
          </Router>
        </MockedProvider>
      </AppContext.Provider>
    </ThemeProvider>
  );

  return rtlRender(ui, { wrapper, ...renderOptions });
};

export const render = (ui: ReactChildren, graphqlMocks?: Array<MockedResponse>) => {
  const wrapper = ({ children }: { children: ReactChildren }) => (
    <MockedProvider addTypename={false} mocks={graphqlMocks}>
      {children}
    </MockedProvider>
  );

  return rtlRender(ui, { wrapper });
};

export const defaultGraphqlMocks = globalMocks;

export const mockFeatureToggles = ({
  toggles = [],
  toggled = true,
  defaultToggle = true,
}: {
  toggles?: FeatureToggleKey[];
  toggled?: boolean;
  defaultToggle?: boolean;
}) => {
  vi.spyOn(ConfigCat, "useFeatureFlag").mockImplementation((key: string, defaultValue: any, user?: any) => ({
    value: toggles.map((featureKey) => FeatureToggleKeyMap[featureKey]).includes(key) ? toggled : defaultToggle,
    loading: false,
  }));
};

export const oktaAuthNotLoggedIn = new OktaAuth({
  issuer: 'https://{yourOktaDomain}.com/oauth2/{authServerId}"',
  clientId: "OKTA_CLIENT_ID",
  redirectUri: window.location.origin + "/login/callback",
});

export const oktaAuthLoggedIn = new OktaAuth({
  issuer: 'https://{yourOktaDomain}.com/oauth2/{authServerId}"',
  clientId: "OKTA_CLIENT_ID",
  redirectUri: window.location.origin + "/login/callback",
  transformAuthState: async (oktaAuth, authState) => {
    authState.isAuthenticated = true;
    oktaAuth.storageManager.getTokenStorage().setItem("accessToken", { accessToken: "eyJraWQ" });
    return authState;
  },
});

export const mockAppContextValue = {
  userAccountInfo: {
    id: demoAccount.id,
    tenantId: demoAccount.tenantId,
    firstName: demoAccount.firstName,
    lastName: demoAccount.lastName,
    email: demoAccount.email,
    roles: [UserRoles.RoleAdmin],
    status: demoAccount.status,
    accountType: demoAccount.linkedRegistries[0].accountType,
    linkedRegistries: demoAccount.linkedRegistries,
    loaded: true,
  },
  setUserAccountInfo: () => {},
  userSelections: {
    registry: demoAccount.linkedRegistries[0],
  },
  setUserSelections: () => {},
};

export const renderHookWithOnboardingContext = <TProp extends { children: ReactChildren }, TResult>(
  hook: (props: TProp) => TResult,
  onboardingContextValue: OnboardingStepperHandler,
  appContextValue: AppContextType = mockAppContextValue,
  formikValues: Record<string, any> = {},
  mocks: MockedResponse[] = [],
) => {
  const wrapper = ({ children }: { children: ReactChildren }) => (
    <AppContext.Provider value={appContextValue}>
      <MockedProvider mocks={mocks}>
        <OnboardingStepperContext.Provider value={onboardingContextValue}>
          <Formik initialValues={formikValues} onSubmit={() => {}}>
            {children}
          </Formik>
        </OnboardingStepperContext.Provider>
      </MockedProvider>
    </AppContext.Provider>
  );

  return rtlRenderHook<TProp, TResult>(hook, { wrapper });
};

export const renderHookWithAppContext = <TProp extends { children: ReactChildren }, TResult>(
  hook: (props: TProp) => TResult,
  contextValue: AppContextType = mockAppContextValue,
  mocks: MockedResponse[] = [],
  history = appHistory,
) => {
  const wrapper = ({ children }: { children: ReactChildren }) => (
    <AppContext.Provider value={contextValue}>
      <MockedProvider mocks={mocks}>
        <Router navigator={history} location={history.location}>
          {children}
        </Router>
      </MockedProvider>
    </AppContext.Provider>
  );

  return rtlRenderHook<TProp, TResult>(hook, { wrapper });
};
